import { Box, Grid, Typography, makeStyles, Theme, CircularProgress } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useState, useEffect } from 'react';
import { CustomTaskDialog, useIsUploadLocked } from '@roc/feature-general-tasks';
import { FileUploadArea, UploadedDocument, Button, StatusDiv, FileUpload } from '@roc/ui';
import { useStore } from '@roc/client-portal-shared/hooks';
import { DocReviewSection } from './docReviewSection';
import { DocReviewTableResolver, DocReviewPreSection, docReviewSectionLayout } from './docReviewTableResolver';
import { DocReviewEditModal } from './genericDocReview/docReviewEditModal';
import { fieldsForDocument } from './genericDocReview/borrowerEntity/entityReviewFields';
import { HudFundingTemplateTable } from './genericDocReview/attorneyHud/hudFundingTemplateTable';
import { emptyTableText } from '../utils/utils';

const useStyles = makeStyles((theme: Theme) => ({
  sectionLabel: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  emptyContent: {
    padding: theme.spacing(3, 2),
    minHeight: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

export interface DocReviewDialogV2Props {
  open: boolean;
  handleClose: () => void;
  taskId: number;
}

export const DocReviewDialogV2 = observer(({
  open,
  handleClose,
  taskId,
}: DocReviewDialogV2Props) => {
  const classes = useStyles();
  const { docReviewStoreV2, documentStore } = useStore();
  const isInternal = docReviewStoreV2.globalStore.isInternalPortal;
  const isAttorney = docReviewStoreV2.globalStore.isAttorneyPortal;
  const [editModalOpen, setEditModalOpen] = useState(false);
  const document = docReviewStoreV2.document;
  const uploadLocked = useIsUploadLocked(document?.status);
  const isBorrowerEntityDoc = fieldsForDocument(document?.taskName ?? '').length > 0;
  const showReviewSection = isInternal || (isAttorney && isBorrowerEntityDoc);

  useEffect(() => {
    if (!open || !taskId) return;
    docReviewStoreV2.getReviewTaskData(taskId);
  }, [open, taskId]);

  const onClose = () => {
    docReviewStoreV2.reset();
    handleClose();
  };

  const handleReupload = async (file: File) => {
    await documentStore.uploadDocument(file, document, document.loanProcess);
    await docReviewStoreV2.getReviewTaskData(taskId);
    docReviewStoreV2.setProcessingStatus('IN_PROGRESS');
  };

  const handleSetFiles = (updater: FileUpload[] | ((prev: FileUpload[]) => FileUpload[])) => {
    const resolved = typeof updater === 'function' ? updater([]) : updater;
    const file = resolved[0]?.file;
    if (file) handleReupload(file);
  };

  const dialogContent = (
    <Grid container spacing={2}>
      {(document?.dropboxPath || !uploadLocked) && (
        <Grid item xs={12}>
          <Typography className={classes.sectionLabel}>
            Upload Document
          </Typography>
          {document?.dropboxPath ? (
            <UploadedDocument
              document={document}
              onUpload={uploadLocked ? undefined : handleReupload}
              disabled={docReviewStoreV2.processingStatus === 'IN_PROGRESS'}
            />
          ) : (
            <FileUploadArea items={[]} setItems={handleSetFiles} />
          )}
        </Grid>
      )}
      {docReviewStoreV2.processingStatus &&
        (isInternal || docReviewStoreV2.processingStatus === 'IN_PROGRESS') && (
          <Grid item xs={12}>
            <StatusDiv
              status={docReviewStoreV2.processingStatus}
              text={docReviewStoreV2.statusText}
              onRefresh={
                isInternal
                  ? () => docReviewStoreV2.getReviewTaskData(document.taskId)
                  : undefined
              }
            />
          </Grid>
        )}
      {isInternal && Object.keys(docReviewStoreV2.docReviewData).length > 0 && (
        <Grid item xs={12}>
          <DocReviewPreSection
            documentName={document?.taskName ?? ''}
            data={docReviewStoreV2.docReviewData}
          />
        </Grid>
      )}
      {showReviewSection && (
        <Grid item xs={12}>
          <DocReviewSection
            onEditClick={
              docReviewStoreV2.processingStatus != 'IN_PROGRESS' &&
              docReviewStoreV2.canShowEditButton(document)
                ? () => setEditModalOpen(true)
                : undefined
            }
            editModal={
              docReviewStoreV2.canShowEditButton(document) && (
                <DocReviewEditModal
                  open={editModalOpen}
                  onClose={() => setEditModalOpen(false)}
                  document={document}
                />
              )
            }
            hideEdit={!docReviewStoreV2.canShowEditButton(document)}
            {...docReviewSectionLayout(document?.taskName ?? '')}
          >
            {Object.keys(docReviewStoreV2.docReviewData).length > 0 ? (
              <DocReviewTableResolver
                documentName={document?.taskName ?? ''}
                data={docReviewStoreV2.docReviewData}
              />
            ) : (
              <Box className={classes.emptyContent}>
                <Typography className={classes.emptyText}>
                  {emptyTableText[docReviewStoreV2.processingStatus] ??
                    'No data extracted yet. Upload a document to get started.'}
                </Typography>
              </Box>
            )}
          </DocReviewSection>
        </Grid>
      )}
      {isInternal &&
        Array.isArray(docReviewStoreV2.docReviewData.fundingTemplate) &&
        docReviewStoreV2.docReviewData.fundingTemplate.length > 0 && (
          <Grid item xs={12}>
            <DocReviewSection title="Funding Template">
              <HudFundingTemplateTable
                items={docReviewStoreV2.docReviewData.fundingTemplate}
              />
            </DocReviewSection>
          </Grid>
        )}
    </Grid>
  );

  const dialogActions = (
    <Grid container justifyContent="flex-end">
      <Grid item>
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          testId="close"
        >
          Close
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <>
      {taskId && (
        <CustomTaskDialog
          open={open}
          maxWidth="md"
          title={document?.title}
          taskId={taskId}
          allowVertScroll={false}
          handleClose={onClose}
          dialogContent={dialogContent}
          dialogActions={dialogActions}
        />
      )}
    </>
  );
});
